import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router-dom";

import { PlacesLibraryCreateButton } from "@/components/ui/admin/places/PlacesLibraryCreateButton";
import { PlacesLibraryKpis as PlacesLibraryKpisPanel } from "@/components/ui/admin/places/PlacesLibraryKpis";
import { PlacesLibraryPanel } from "@/components/ui/admin/places/PlacesLibraryPanel";
import { createPlaceService } from "@/core/services/factory";
import {
  parsePlaceLibraryTab,
  parsePlaceLibrarySort,
  parsePlacePage,
  parsePlacePageSize,
  parsePlaceSearchQuery,
  type PlaceAdminListItem,
} from "@/core/services/types/place.types";
import { useLibraryResultsFreeze } from "@/hooks/useLibraryResultsFreeze";
import { useLibrarySearchSnapshot } from "@/hooks/useLibrarySearchSnapshot";
import { useTranslations } from "@/lib/useTranslations";

export function PlacesLibraryPage() {
  useLibrarySearchSnapshot("places");
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tab = parsePlaceLibraryTab(searchParams.get("status") ?? undefined);
  const page = parsePlacePage(searchParams.get("page") ?? undefined);
  const pageSize = parsePlacePageSize(searchParams.get("size") ?? undefined);
  const searchQuery = parsePlaceSearchQuery(searchParams.get("q") ?? undefined);
  const sort = parsePlaceLibrarySort(
    searchParams.get("sort") ?? undefined,
    searchParams.get("dir") ?? undefined,
  );
  const t = useTranslations("places.library");
  const queryFingerprint = location.search;

  const { data: stats } = useQuery({
    queryKey: ["places-library-stats"],
    queryFn: async () => {
      const places = createPlaceService();
      return places.getLibraryStats();
    },
    staleTime: 60_000,
  });

  const { data: listResult, isFetching: isListFetching } = useQuery({
    queryKey: ["places-library-list", tab, page, pageSize, searchQuery, sort?.column, sort?.direction],
    queryFn: async () => {
      const places = createPlaceService();
      return places.listByLibraryTab(tab, page, pageSize, searchQuery, sort);
    },
    placeholderData: keepPreviousData,
  });

  const {
    isFrozen,
    displayResult,
    toggleFreeze,
    canFreeze,
  } = useLibraryResultsFreeze<PlaceAdminListItem>(
    "places",
    queryFingerprint,
    listResult,
  );

  if (!stats || !displayResult) {
    return (
      <div className="mx-auto flex w-full flex-1 items-center justify-center px-4 py-10 text-sm text-neutral-500 lg:px-8">
        Chargement…
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-1 flex-col gap-6 overflow-y-auto px-4 py-10 lg:px-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium uppercase tracking-widest text-neutral-500">
            {t("eyebrow")}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-whart-text">
            {t("title")}
          </h1>
          <p className="text-sm text-neutral-600">{t("description")}</p>
        </div>
        <PlacesLibraryCreateButton />
      </header>

      <PlacesLibraryKpisPanel
        kpis={stats.kpis}
        sectionLabel={t("kpisNav")}
        labels={{
          total: t("kpis.total"),
          publishedThisMonth: t("kpis.publishedThisMonth"),
          pendingReview: t("kpis.pendingReview"),
        }}
      />

      <PlacesLibraryPanel
        items={displayResult.items}
        total={displayResult.total}
        page={displayResult.page}
        pageSize={displayResult.pageSize}
        tab={tab}
        tabCounts={stats.tabCounts}
        searchQuery={searchQuery}
        sort={sort}
        isFrozen={isFrozen}
        onToggleResultsFreeze={toggleFreeze}
        canFreezeResults={canFreeze}
        isRefreshing={!isFrozen && isListFetching}
      />
    </div>
  );
}
